import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { RECAPTCHA_SITE_KEY } from '../config';

interface IGrecaptchaApi {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

interface IGrecaptchaWindow extends Window {
  grecaptcha?: IGrecaptchaApi;
}

/** Thrown when no token could be obtained, so callers can tell it apart. */
export class RecaptchaError extends Error {}

const API_JS_URL = 'https://www.google.com/recaptcha/api.js';
const SCRIPT_ID = 'ntt-recaptcha-script';
const POLL_INTERVAL_MS = 50;
const READY_TIMEOUT_MS = 10000;
const EXECUTE_DEADLINE_MS = 15000;

@Injectable({
  providedIn: 'root',
})
export class RecaptchaService {
  private readonly _document = inject(DOCUMENT);
  private readonly _siteKey = inject(RECAPTCHA_SITE_KEY);
  private readonly _window = this._document.defaultView as IGrecaptchaWindow;

  readonly isReady = signal(false);

  private _loadPromise: Promise<void> | null = null;

  get enabled(): boolean {
    return !!this._siteKey;
  }

  /**
   * Loads reCAPTCHA and resolves once it is ready to issue tokens.
   */
  load(): Promise<void> {
    if (!this.enabled)
      return Promise.reject(new RecaptchaError('reCAPTCHA is disabled.'));

    this._loadPromise ??= this._injectScript();

    return this._loadPromise;
  }

  /**
   * Removes the script, the badge Google renders into the document and the
   * global API, so the badge is absent from the DOM for as long as the login
   * page is not mounted and the next visit starts from a fresh client.
   */
  teardown(): void {
    this._document.getElementById(SCRIPT_ID)?.remove();
    this._removeBadge();
    this._loadPromise = null;
    this.isReady.set(false);
    delete this._window.grecaptcha;
  }

  /**
   * Returns a single use token for the given action. Tokens expire after two
   * minutes, so this must be called right before the request it protects.
   * Rejects when no token comes back, so callers never send an empty value.
   */
  async execute(action: string): Promise<string> {
    if (!this.enabled) {
      throw new RecaptchaError('reCAPTCHA is disabled.');
    }

    return Promise.race([this._executeWithRetry(action), this._deadline()]);
  }

  private async _executeWithRetry(action: string): Promise<string> {
    try {
      return await this._execute(action);
    } catch {
      // A client kept alive across SPA navigation, or one Google has throttled
      // after too many executes, stops handing out tokens, which is why
      // reloading the page used to be the only way out. Rebuild it once before
      // giving up.
      this.teardown();

      return this._execute(action);
    }
  }

  private _deadline(): Promise<never> {
    return new Promise<never>((_, reject) => {
      this._window.setTimeout(
        () => reject(new RecaptchaError('reCAPTCHA did not return in time.')),
        EXECUTE_DEADLINE_MS,
      );
    });
  }

  private async _execute(action: string): Promise<string> {
    await this.load();

    const grecaptcha = this._window.grecaptcha;
    if (!grecaptcha) {
      throw new RecaptchaError('reCAPTCHA API is not available.');
    }

    const token = await grecaptcha.execute(this._siteKey, { action });

    if (!token) {
      throw new RecaptchaError('reCAPTCHA returned an empty token.');
    }

    return token;
  }

  private _injectScript(): Promise<void> {
    if (this._document.getElementById(SCRIPT_ID)) {
      return this._whenReady();
    }

    return new Promise<void>((resolve, reject) => {
      const script = this._document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `${API_JS_URL}?render=${this._siteKey}`;
      script.async = true;
      script.defer = true;

      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener('error', () => this._fail(reject), {
        once: true,
      });

      this._document.head.appendChild(script);
    }).then(() => this._whenReady());
  }

  /**
   * Polls rather than only listening for `load`, because a script tag that is
   * already in the document has no `load` event left to observe.
   */
  private _whenReady(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timeoutId = this._window.setTimeout(
        () =>
          this._fail(reject, 'Timed out while waiting for the reCAPTCHA API.'),
        READY_TIMEOUT_MS,
      );

      const poll = () => {
        const grecaptcha = this._window.grecaptcha;
        if (!grecaptcha) {
          this._window.setTimeout(poll, POLL_INTERVAL_MS);
          return;
        }

        grecaptcha.ready(() => {
          this._window.clearTimeout(timeoutId);
          this.isReady.set(true);
          resolve();
        });
      };

      poll();
    });
  }

  /**
   * The badge is an iframe Google appends to the body, separate from the
   * script tag, so removing the script alone does not remove it.
   */
  private _removeBadge(): void {
    this._document
      .querySelectorAll('.grecaptcha-badge')
      .forEach((badge) => badge.remove());
  }

  /** Discards the cached attempt so the next `load()` starts over. */
  private _fail(
    reject: (reason: Error) => void,
    message = 'Failed to load reCAPTCHA.',
  ): void {
    this._loadPromise = null;
    reject(new RecaptchaError(message));
  }
}
